import React, { useEffect, useState } from "react";
import TextInput from "../../common/textinput";
import RadioButton from "../../common/radio-button";
import Dropdown from "../../common/dropdown";
import Loader from "../../common/loader";
import { useAppSelector } from "../../../store/hooks/StoreHooks";
import useWellFacade from "../../../facade/calculation/calculation-input/well.Facade";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../store/ConfigureStore";
import useCollapseFacade from "../../../facade/collapse.Facade";
import Accordion from "../../common/accordion";
import useCalculationFacade from "../../../facade/calculation/calculation.facade";
import { HelpCircle } from "../../../assets/icons";

const WellInput: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    updateTubingPressure,
    updateCasingPressure,
    updateInflowTypeLstVal,
    updateProductivityIndex,
    updateStaticBottomHolePressurePI,
    updateStaticFluidLevelFromSurfacePI,
    updateDesiredProductionRate,
    updateStaticBottomHolePressureP,
    updateFlowingBottomHolePressureP,
    updateTestFlowRate,
    updateStaticFluidLevelFromSurfaceHead,
    updateProducingFluidLevelFromSurfaceHead,
    updateTestFlowRateHead,
    updateSettingDepth,
    updatePumpSettingVerticalDepth,
    updateReservoirDatum,
    updateHiddenHeadPIIPType,
  } = useWellFacade();
  const { updateSettingDepthReference } = useCalculationFacade();

  const { loading } = useAppSelector((state) => state?.wellInput);
  const { customerEnabled } = useAppSelector((state) => state.auth);
  const { projectReadOnly } = useAppSelector((state) => state.projectHeader);
  const { schematicData } = useAppSelector((state) => state?.calculations);
  const { tab2Input } = useAppSelector((state) => state?.wellInput);

  const {
    updateSurfaceConditionsCollapse,
    updateIprTypeCollapse,
    updateSettingDepthCollapse,
  } = useCollapseFacade();

  const { surfaceConditionsCollapse, iprTypeCollapse, settingDepthCollapse } =
    useAppSelector((state) => state?.collapse);

  const [wellTab, setWellTab] = useState({
    pumpSetting: tab2Input?.pumpSetting,
    tvd: tab2Input?.tvd,
    perforation: tab2Input?.perforation
  });

  useEffect(() => {
    if (tab2Input) {
      setWellTab((prev) => ({
        ...prev,
        pumpSetting: tab2Input?.pumpSetting,
        tvd: tab2Input?.tvd,
        perforation: tab2Input?.perforation
      }));
    }
  }, [tab2Input]);

  const inflowTypeOptions = [
    { id: "0", name: "PI" },
    { id: "1", name: "Pressure" },
    { id: "2", name: "Head" },
  ];
  const handleSelect = (option: { id: string; name: string } | undefined) => {
    if (!option) return;
    updateInflowTypeLstVal(option?.id); // Update Redux state
  };

  const handleRadioChange1 = (
    option: "reservoirPressure" | "staticFluidLvl"
  ) => {
    if (option === "reservoirPressure") {
      updateHiddenHeadPIIPType("0");
    } else {
      updateHiddenHeadPIIPType("1");
    }
  };

  const handleRadioChange = (value: number) => {
    updateSettingDepthReference(value);
  };

  type InputFields = keyof typeof inputToApiMap;
  // Mapping inputs to their corresponding APIs
  const inputToApiMap = {
    tubingPressureVal: ["tubingPressureVal"],
    casingPressureVal: ["casingPressureVal"],
    staticFluidLevelFromSurfaceHeadVal: ["staticFluidLevelFromSurfaceHeadVal"],
    producingFluidLevelFromSurfaceHeadVal: [
      "producingFluidLevelFromSurfaceHeadVal",
    ],
    testFlowRateHeadVal: ["testFlowRateHeadVal"],
    productivityIndexVal: ["productivityIndexVal"],
    staticBottomHolePressurePIVal: ["staticBottomHolePressurePIVal"],
    staticFluidLevelFromSurfacePIVal: [
      "staticFluidLevelFromSurfacePIVal",
      "staticFluidChange",
    ],
    flowingBottomHolePressurePVal: ["flowingBottomHolePressurePVal"],
    staticBottomHolePressurePVal: ["staticBottomHolePressurePVal"],
    testFlowRateVal: ["testFlowRateVal"],
    desiredProductionRateVal: ["desiredProductionRateVal"],
    settingDepthVal: ["settingDepthVal"],
    pumpSettingVerticalDepthVal: ["pumpSettingVerticalDepthVal"],
    reservoirDatumVal: ["reservoirDatumVal"],
  } as const;

  // Handle input changes
  const handleInputChange = (field: InputFields, value: any) => {
    // Dispatch Redux actions dynamically based on the field
    const updateActionMap: Partial<Record<string, any>> = {
      tubingPressureVal: updateTubingPressure,
      casingPressureVal: updateCasingPressure,
      staticFluidLevelFromSurfaceHeadVal: updateStaticFluidLevelFromSurfaceHead,
      producingFluidLevelFromSurfaceHeadVal:
        updateProducingFluidLevelFromSurfaceHead,
      testFlowRateHeadVal: updateTestFlowRateHead,
      productivityIndexVal: updateProductivityIndex,
      staticBottomHolePressurePIVal: updateStaticBottomHolePressurePI,
      staticFluidLevelFromSurfacePIVal: updateStaticFluidLevelFromSurfacePI,
      flowingBottomHolePressurePVal: updateFlowingBottomHolePressureP,
      staticBottomHolePressurePVal: updateStaticBottomHolePressureP,
      testFlowRateVal: updateTestFlowRate,
      desiredProductionRateVal: updateDesiredProductionRate,
      settingDepthVal: updateSettingDepth,
      pumpSettingVerticalDepthVal: updatePumpSettingVerticalDepth,
      reservoirDatumVal: updateReservoirDatum,
    };
    const numberRegex = /^(?:\d+(\.\d*)?|\.\d+)?$/;
    const sbhpPIregex = /^-?(\d+(\.\d*)?|\.\d+)?$/;
    const decimalRegex = /^\d*\.?\d*$/;
    const action = updateActionMap[field];
    if (field === "settingDepthVal" && decimalRegex.test(value)) {
      setWellTab((prev) => ({ ...prev, pumpSetting: value }));
    } else if (field === "pumpSettingVerticalDepthVal" && decimalRegex.test(value)) {
      setWellTab((prev) => ({ ...prev, tvd: value }));
    } else if (field === "reservoirDatumVal" && decimalRegex.test(value)) {
      setWellTab((prev) => ({ ...prev, perforation: value }));
    } else if (
      field === "staticBottomHolePressurePIVal" &&
      sbhpPIregex.test(value)
    ) {
      if (action) {
        dispatch(action(Number(value)));
      }
    } else if (value === "" || numberRegex.test(value)) {
      if (action) {
        dispatch(action(Number(value)));
      }
    }
  };

  const handleBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = e?.target;
    if (name === "settingDepth") {
      updateSettingDepth(wellTab?.pumpSetting ?? 0);
    } else if (name === "tvd") {
      updatePumpSettingVerticalDepth(wellTab?.tvd ?? 0);
    } else if (name === "reservoir") {
      updateReservoirDatum(wellTab?.perforation ?? 0);
    }
  };

  const handleCollapseChange = (key: boolean, name: string) => {
    if (name === "surface-condition") updateSurfaceConditionsCollapse(key);
    else if (name === "ipr-type") updateIprTypeCollapse(key);
    else if (name === "setting-depth") updateSettingDepthCollapse(key);
  };

  return (
    <>
      <Loader isLoading={loading} />
      <div className="flex flex-col gap-[16px]">
        <Accordion
          title="Surface conditions"
          isOpen={surfaceConditionsCollapse}
          onToggle={() =>
            handleCollapseChange(
              !surfaceConditionsCollapse,
              "surface-condition"
            )
          }
          content={
            <div className="flex flex-col gap-[16px] p-[12px]">
              <TextInput
                label="Tubing pressure"
                required={true}
                placeholder=""
                name="tubingPressure"
                value={tab2Input?.tubingPressure || ""}
                unit="psi"
                onChange={(e) =>
                  handleInputChange("tubingPressureVal", e.target.value)
                }
              />
              <TextInput
                label="Casing pressure"
                placeholder=""
                name="casingPressure"
                value={tab2Input?.casingPressure || ""}
                unit="psi"
                onChange={(e) =>
                  handleInputChange("casingPressureVal", e.target.value)
                }
              />
            </div>
          }
        />

        <Accordion
          title="IPR type"
          isOpen={iprTypeCollapse}
          onToggle={() => handleCollapseChange(!iprTypeCollapse, "ipr-type")}
          content={
            <div className="flex flex-col gap-[16px] p-[12px]">
              {/* InFlow Type Dropdown */}
              <Dropdown
                options={inflowTypeOptions}
                bgColor="bg-transparent"
                dropdownBgColor="bg-darkcolor-800"
                placeholder=""
                label="Inflow Type"
                selected={tab2Input?.inflowTypeLstVal?.toString()}
                onSelect={(e) => handleSelect(e)}
              />

              {/* PI Section */}
              {tab2Input?.inflowTypeLstVal === "0" && (
                <>
                  <TextInput
                    label="Productivity index (PI)"
                    required={true}
                    placeholder="Enter PI value"
                    name="piVal"
                    value={tab2Input?.piVal || ""}
                    unit="bpd/psi"
                    onChange={(e) =>
                      handleInputChange("productivityIndexVal", e.target.value)
                    }
                  />
                  <div className="flex items-center space-x-4">
                    <RadioButton
                      checked={tab2Input?.hiddenPresHeadPIIPType === "0"}
                      onChange={() => handleRadioChange1("reservoirPressure")}
                    />
                    <TextInput
                      label="Static bottom hole pressure"
                      required={true}
                      placeholder="Enter reservoir pressure"
                      name="reservoirPressure"
                      value={tab2Input?.reservoirPressure || ""}
                      unit="psi"
                      disabled={tab2Input?.hiddenPresHeadPIIPType === "1"}
                      onChange={(e) =>
                        handleInputChange(
                          "staticBottomHolePressurePIVal",
                          e.target.value
                        )
                      }
                    />
                  </div>
                  <div className="flex items-center space-x-4">
                    <RadioButton
                      checked={tab2Input?.hiddenPresHeadPIIPType === "1"}
                      label=""
                      onChange={() => handleRadioChange1("staticFluidLvl")}
                    />
                    <TextInput
                      label="Static fluid level from surface"
                      placeholder="Enter Static Fluid Level From Surface"
                      required={true}
                      name="staticFluidLv1"
                      value={tab2Input?.staticFluidLvl || ""}
                      unit="ft"
                      disabled={tab2Input?.hiddenPresHeadPIIPType === "0"}
                      onChange={(e) =>
                        handleInputChange(
                          "staticFluidLevelFromSurfacePIVal",
                          e.target.value
                        )
                      }
                    />
                  </div>
                </>
              )}

              {/* Pressure Section */}
              {tab2Input?.inflowTypeLstVal === "1" && (
                <div className="space-y-4">
                  <TextInput
                    label="Static bottom hole pressure"
                    placeholder="Enter static bottom hole pressure"
                    required={true}
                    name="staticBottomHolePressure"
                    value={tab2Input?.sbhpForPressre || ""}
                    unit="psi"
                    onChange={(e) =>
                      handleInputChange(
                        "staticBottomHolePressurePVal",
                        e.target.value
                      )
                    }
                  />
                  <TextInput
                    label="Flowing bottom hole pressure"
                    placeholder="Flowing bottom hole pressure"
                    required={true}
                    name="intakePressure"
                    value={tab2Input?.intakePressure || ""}
                    unit="psi"
                    onChange={(e) =>
                      handleInputChange(
                        "flowingBottomHolePressurePVal",
                        e.target.value
                      )
                    }
                  />
                  <TextInput
                    label="Test flow rate"
                    required={true}
                    placeholder="Enter flow rate"
                    name="flowRate"
                    value={tab2Input?.flowRate || ""}
                    unit="bpd"
                    onChange={(e) =>
                      handleInputChange("testFlowRateVal", e.target.value)
                    }
                  />
                </div>
              )}

              {/* Head Section */}
              {tab2Input?.inflowTypeLstVal === "2" && (
                <>
                  <TextInput
                    label="Static fluid level from surface"
                    required={true}
                    placeholder="Enter static fluid level from surface"
                    name="staticFluidLv1Head"
                    value={tab2Input?.staticFluidLvlForHd || ""}
                    unit="ft"
                    onChange={(e) =>
                      handleInputChange(
                        "staticFluidLevelFromSurfaceHeadVal",
                        e.target.value
                      )
                    }
                  />
                  <TextInput
                    label="Producing fluid level surface"
                    required={true}
                    placeholder="Enter producing fluid level surface"
                    name="producingFluidLevelSurface"
                    value={tab2Input?.prdcngFluidLvl || ""}
                    unit="ft"
                    onChange={(e) =>
                      handleInputChange(
                        "producingFluidLevelFromSurfaceHeadVal",
                        e.target.value
                      )
                    }
                  />
                  <TextInput
                    label="Test flow rate"
                    placeholder="Enter test flow rate"
                    required={true}
                    name="flowRateHead"
                    value={tab2Input?.flowRateForHd || ""}
                    unit="bpd"
                    onChange={(e) =>
                      handleInputChange("testFlowRateHeadVal", e.target.value)
                    }
                  />
                </>
              )}

              {/* Desired Production Rate */}
              <TextInput
                name="productionRate"
                value={tab2Input?.productionRate || ""}
                required={true}
                label="Desired production rate"
                placeholder="Enter production rate"
                unit={"bpd"}
                onChange={(e) =>
                  handleInputChange("desiredProductionRateVal", e.target.value)
                }
              />
            </div>
          }
        />
        {/* Accordion 2: IPR Type */}
        {/* Setting Depth */}
        <Accordion
          title="Setting depth"
          isOpen={settingDepthCollapse}
          onToggle={() =>
            handleCollapseChange(!settingDepthCollapse, "setting-depth")
          }
          content={
            <div className="flex flex-col gap-[16px] p-[12px]">
              {(!customerEnabled || !projectReadOnly) && (
                <div className="flex">
                  <label style={{ color: "#F7F9F9" }}>
                    Setting Depth Reference
                  </label>
                  <span className="text-error-600 ml-1">*</span>
                </div>
              )}
              {(!customerEnabled || !projectReadOnly) && (
                <div className="flex space-x-2 overflow-x-auto">
                  <RadioButton
                    checked={
                      schematicData?.miscellaneous?.setDepthType === 0
                    }
                    label="Sensor"
                    onChange={() => handleRadioChange(0)}
                  />
                  <RadioButton
                    checked={
                      schematicData?.miscellaneous?.setDepthType === 1 
                    }
                    label="Intake"
                    onChange={() => handleRadioChange(1)}
                  />
                  <RadioButton
                    checked={
                      schematicData?.miscellaneous?.setDepthType === 2
                    }
                    label="SetDepth (btm)"
                    onChange={() => handleRadioChange(2)}
                  />
                </div>
              )}

              <TextInput
                label={(customerEnabled || projectReadOnly) ? "Setting Depth Reference" : ""}
                name="settingDepth"
                placeholder="Enter setting depth"
                value={wellTab?.pumpSetting || ""}
                unit="ft"
                onChange={(e) =>
                  handleInputChange("settingDepthVal", e.target.value)
                }
                onBlur={(e) => handleBlur(e)}
              />
              <TextInput
                name="tvd"
                required={true}
                label="Pump setting @ vertical depth"
                placeholder="Enter pump setting"
                value={wellTab?.tvd || ""}
                unit="ft"
                onChange={(e) =>
                  handleInputChange(
                    "pumpSettingVerticalDepthVal",
                    e.target.value
                  )
                }
                onBlur={(e) => handleBlur(e)}
              />
              <TextInput
                name="reservoir"
                label="Reservoir DATUM"
                required={true}
                placeholder="Enter reservoir DATUM"
                value={wellTab?.perforation || ""}
                unit="ft"
                onChange={(e) =>
                  handleInputChange("reservoirDatumVal", e.target.value)
                }
                onBlur={(e) => handleBlur(e)}
              />
              <div className="flex items-center justify-between text-[#F7F9F9]">
                <div className="flex items-center space-x-2">
                  <label>FLOP @ Desired Production Rate</label>
                  <a href="/help" target="_blank" rel="noopener noreferrer">
                    <HelpCircle className="cursor-pointer" />
                  </a>
                </div>
                <label className="mr-2 text-right">
                  {tab2Input?.flop ?? "0"} ft
                </label>
              </div>
            </div>
          }
        />
      </div>
    </>
  );
};

export default WellInput;
